import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, ArrowRight, ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Read ?next=… from query string so we can redirect users back where they were
  // after a forced logout (e.g. axios 401 interceptor in AuthContext).
  const nextPath = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('next');
    if (!raw) return '/dashboard';
    try {
      const decoded = decodeURIComponent(raw);
      // Only allow internal paths to avoid open-redirect risks
      return decoded.startsWith('/') && !decoded.startsWith('//') ? decoded : '/dashboard';
    } catch {
      return '/dashboard';
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate(nextPath);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotSending(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      setForgotError(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setForgotSending(false);
    }
  };

  const backToLogin = () => {
    setForgotMode(false);
    setForgotSent(false);
    setForgotError('');
    setForgotEmail('');
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="login-page">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-12">
            <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center">
              <span className="text-white font-heading font-bold">SEO</span>
            </div>
            <span className="font-heading font-bold text-xl text-neutral-900">Agency</span>
          </div>

          <h1 className="font-heading font-bold text-3xl text-neutral-900 mb-2">
            {forgotMode ? 'Mot de passe oublie' : 'Connexion'}
          </h1>
          <p className="text-neutral-500 mb-8">
            {forgotMode
              ? 'Entrez votre email pour recevoir un lien de reinitialisation'
              : 'Accedez a votre tableau de bord SEO'}
          </p>

          {/* Forgot password flow */}
          {forgotMode ? (
            <>
              {forgotSent ? (
                <div className="text-center py-6" data-testid="forgot-success">
                  <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-3" />
                  <p className="text-neutral-900 font-medium">Email envoye !</p>
                  <p className="text-neutral-500 text-sm mt-2">
                    Si un compte existe avec l'email <strong>{forgotEmail}</strong>, vous recevrez un lien pour reinitialiser votre mot de passe.
                  </p>
                  <p className="text-neutral-400 text-xs mt-3">
                    Verifiez vos spams si vous ne voyez rien dans votre boite de reception.
                  </p>
                  <Button variant="outline" className="mt-6" onClick={backToLogin} data-testid="back-to-login-btn">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Retour a la connexion
                  </Button>
                </div>
              ) : (
                <>
                  {forgotError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
                      {forgotError}
                    </div>
                  )}
                  <form onSubmit={handleForgotPassword} className="space-y-6">
                    <div>
                      <Label htmlFor="forgot-email" className="text-neutral-700 font-medium">Email</Label>
                      <Input id="forgot-email" type="email" value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="vous@example.com"
                        className="mt-2 h-12 border-neutral-200 focus:border-[#002FA7] focus:ring-[#002FA7]"
                        required data-testid="forgot-email-input" />
                    </div>
                    <Button type="submit" disabled={forgotSending}
                      className="w-full h-12 bg-[#002FA7] hover:bg-[#002277] text-white font-medium"
                      data-testid="forgot-submit-btn">
                      {forgotSending ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Envoi...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Mail className="w-4 h-4" /> Envoyer le lien
                        </span>
                      )}
                    </Button>
                  </form>
                  <p className="mt-6 text-center">
                    <button onClick={backToLogin} className="text-[#002FA7] hover:underline font-medium text-sm"
                      data-testid="back-to-login-link">
                      <ArrowLeft className="w-3 h-3 inline mr-1" /> Retour a la connexion
                    </button>
                  </p>
                </>
              )}
            </>
          ) : (
            <>
              {/* Normal login flow */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6" data-testid="login-error">
                  {error}
                </div>
              )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-neutral-700 font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@example.com"
                className="mt-2 h-12 border-neutral-200 focus:border-[#002FA7] focus:ring-[#002FA7]"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-neutral-700 font-medium">
                Mot de passe
              </Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Votre mot de passe"
                  className="h-12 border-neutral-200 focus:border-[#002FA7] focus:ring-[#002FA7] pr-12"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="flex justify-end mt-1.5">
                <button type="button" onClick={() => { setForgotMode(true); setForgotEmail(email); setError(''); }}
                  className="text-sm text-[#002FA7] hover:underline" data-testid="forgot-password-link">
                  Mot de passe oublie ?
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#002FA7] hover:bg-[#002277] text-white font-medium"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Connexion...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Se connecter
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-neutral-500">
            Pas encore de compte ?{' '}
            <Link to="/register" className="text-[#002FA7] hover:underline font-medium" data-testid="register-link">
              Creer un compte
            </Link>
          </p>
            </>
          )}
        </div>
      </div>

      {/* Right side - Decorative */}
      <div className="hidden lg:flex flex-1 bg-neutral-900 items-center justify-center p-12">
        <div className="max-w-lg">
          <div className="text-[#002FA7] font-mono text-sm mb-4 tracking-wider">
            // PLATEFORME SEO
          </div>
          <h2 className="font-heading font-bold text-4xl text-white mb-6">
            Gérez votre agence SEO avec précision
          </h2>
          <p className="text-neutral-400 leading-relaxed">
            Suivez les positions de vos mots-clés, générez des rapports clients, 
            et planifiez vos entretiens mensuels depuis une seule plateforme.
          </p>
          
          <div className="mt-12 grid grid-cols-2 gap-6">
            <div className="p-4 border border-neutral-700 rounded">
              <div className="font-mono text-2xl text-white mb-1">+45%</div>
              <div className="text-neutral-500 text-sm">Gain de temps</div>
            </div>
            <div className="p-4 border border-neutral-700 rounded">
              <div className="font-mono text-2xl text-white mb-1">3x</div>
              <div className="text-neutral-500 text-sm">Plus de clients</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
